/**
 *
 * ResourceItemSmall
 *
 */

import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import LazyLoad from 'react-lazyload';
import { makeStyles } from '@material-ui/core/styles';
import CardActionArea from '@material-ui/core/CardActionArea';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { getBlogType } from 'containers/Article/utils';
import { makeSelectProfileFilters } from 'containers/Auth/selectors';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import AudioCard from 'components/AudioCard';
import Badge from 'components/Badge';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full.svg';
import LinkItem from 'components/LinkItemSmall';
import LinkItemNew from 'components/LinkItemSmall/LinkItemNew';
import SurveyResultContext from 'components/SurveyResult/SurveyResultContext';
import TopicItem from 'components/TopicItemSmall';
import VideoEmbedly from 'components/VideoEmbedly';
import { getEmbedlyImageUrl } from 'utils/embedlyUtils';
import Mixpanel from 'utils/mixpanelService';
import { isBot, isIE, fallbackImage } from 'utils/stringUtils';
import { isEmbedded } from 'utils/embedded';
import UserInteractionService from 'utils/userInteractionService';
import { setLeavingModal } from 'containers/Main/actions';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { useSaveToFavorite } from 'components/useSaveToFavorite';
import Image from './components/Image';
import ListImage from './components/ListImage';
import Rating from './components/Rating';
import Title from './components/Title';
import MatchedLabels from './components/MatchedLabels';
import { getMatchedLabels, getClientExclusiveLevel } from './utils';

const PARSED_TYPES_MAP = {
  app: 'Apps',
  book: 'Books',
  podcast: 'Podcasts',
  video: 'Videos',
};

const SHOW_RATINGS_TYPES = ['Apps', 'Books', 'Podcasts', 'Videos'];

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  openWrapper: {
    width: '200%',
    zIndex: 8,
    left: '-50%',
  },
  rightWrapper: {
    left: 0,
  },
  leftWrapper: {
    left: '-100%',
  },
  link: {
    textDecoration: 'none',
    position: 'relative',
  },
  container: {
    overflow: 'hidden',
    borderRadius: '5px !important',
    border: `1px solid #E9ECED`,
    height: 271,
    width: 'auto',
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  clientExclusiveContainer: {
    borderColor: props => props.clientAccent || theme.palette.primary.main,
  },
  clientGroupExclusiveContainer: {
    borderColor: props =>
      props.clientGroupAccent ||
      props.clientAccent ||
      theme.palette.primary.main,
  },
  personalizedContainer: {
    overflow: 'hidden',
    borderRadius: '10px !important',
    border: `1px solid #E9ECED`,
    height: 300,
    width: 'auto',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('480')]: {
      height: 270,
    },
  },
  fromClientAdminPersonalizedContainer: {
    height: '230px !important',
    '@media (min-width: 960px) and (max-width: 1200px)': {
      height: '200px !important',
    },
    '@media (max-width: 960px)': {
      height: '300px !important',
    },
  },
  openContainer: {
    boxShadow: theme.shadows[16],
    border: 0,
  },
  imageVideoContainer: {
    height: 123,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  clientExclusiveLabelWrapper: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: props => props.clientAccent || theme.palette.primary.main,
    zIndex: 2,
  },
  clientGroupExclusiveLabelWrapper: {
    backgroundColor: props =>
      props.clientGroupAccent ||
      props.clientAccent ||
      theme.palette.primary.main,
  },
  clientExclusiveLabel: {
    ...theme.typography.overline,
    color: props => props.clientFontColor || '#FFFFFF',
    textAlign: 'center',
  },
  clientGroupExclusiveLabel: {
    color: props =>
      props.clientGroupFontColor || props.clientFontColor || '#FFFFFF',
  },
  personalizeImageContainer: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  videoContainer: {
    backgroundColor: '#000000',
  },
  blurredBackground: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    filter: ({ personalizedCard }) => !personalizedCard && 'blur(8px)',
    '-webkit-filter': ({ personalizedCard }) =>
      !personalizedCard && 'blur(8px)',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  blurredGrayBackground: {
    filter: ({ personalizedCard }) =>
      !personalizedCard && 'blur(8px) grayscale(1)',
    '-webkit-filter': ({ personalizedCard }) =>
      !personalizedCard && 'blur(8px) grayscale(1)',
  },
  imageWrapper: {
    zIndex: 2,
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentWrapper: {
    padding: '2px 10px 0 10px',
    borderBottomRightRadius: 10,
    borderBottomLeftRadius: 10,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
    height: 148,
    overflowY: 'hidden',
  },
  contentRatingWrapper: {
    height: 124,
    overflow: 'hidden',
  },
  rightContentWrapper: {
    paddingBottom: 2,
  },
  openContentWrapper: {
    '&:first-child': {
      borderRight: '0.5px solid #E9ECED',
    },
    '&:last-child': {
      borderLeft: '0.5px solid #E9ECED',
    },
  },
  badgeAndIconWrapper: {
    position: 'relative',
    marginBottom: 4,
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    padding: 4,
    marginRight: -4,
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  icon: {
    width: 17,
    zIndex: 2,
  },
  desktopWrapper: {
    zIndex: 0,
  },
  subtitle: {
    ...theme.typography.pxSmall,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 54 /* Fallback for non-webkit */,
  },
  shortSubtitle: {
    ...theme.typography.pxSmall,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 36 /* Fallback for non-webkit */,
  },
  personSubtitle: {
    '-webkit-line-clamp': '1',
    maxHeight: 17,
    fontSize: '0.625rem',
  },
  personSubtitleExpanded: {
    '-webkit-line-clamp': '3',
    maxHeight: 51,
    fontSize: '0.625rem',
  },
  personBio: {
    marginTop: 4,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& div': {
      ...theme.typography.pxSmall,
      '& p': {
        ...theme.typography.pxSmall,
      },
    },
  },
  divider: {
    margin: 'auto 3px',
    color: '#D8D8D8',
    fontSize: '0.75rem',
  },
  cardImg: {
    width: '100%',
    height: '100%',
    borderRadius: '10px 10px 0px 0px !important',
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

function ResourceItemSmall({
  item,
  item: {
    shortDescription = '',
    title = '',
    slug,
    name = '',
    image = {},
    alternateImage = {},
    imageUrl = '',
    labels = [],
    url = '',
    minutes = '',
    blog: isBlog,
    type: ctfSubtype,
    subtype: algoliaSubtype,
    organization,
    description,
    cardHighlights,
    audience,
    tags,
    highlights,
    resourcesImages,
    expandedType,
    wordCount,
  },
  listTitle,
  type,
  route,
  recommendation,
  expanded,
  evidence,
  personalizedCard,
  fromClientAdmin,
  isLazyList,
  isPdf,
  grayscale,
  trackingPath,
  asLink = true,
}) {
  const [open] = useState(expanded);
  const hasTracked = useRef(false);
  const wrapperRef = useRef();
  const dispatch = useDispatch();
  const [podcastSubtitle, setPodcastSubtitle] = useState(null);
  const location = useLocation();
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const profileFilters = useSelector(makeSelectProfileFilters());
  const locale = useAlgoliaLocale();
  const classes = useStyles({
    personalizedCard,
    clientAccent: _get(clientDetails, 'metadata.clientExclusiveAccent'),
    clientFontColor: _get(
      clientDetails,
      'metadata.clientExclusiveResource.fontColor',
    ),
    clientGroupAccent: _get(
      clientDetails,
      'clientGroup.metadata.clientExclusiveAccent',
    ),
    clientGroupFontColor: _get(
      clientDetails,
      'clientGroup.metadata.clientExclusiveResource.fontColor',
    ),
  });
  const [siteConfig] = useSiteConfigSelector(['Features']);
  const isDescriptionLessThanThreshold =
    wordCount <= _get(siteConfig, 'config.articlesMinimumWordCount');
  const { answersMatchedAudiences, answersMatchedTags } = useContext(
    SurveyResultContext,
  );
  const { saved, handleSave } = useSaveToFavorite({ item, type, trackingPath });

  const clientExclusiveLevel = _get(
    clientDetails,
    'metadata.clientExclusiveResource.active',
    false,
  )
    ? getClientExclusiveLevel(
        item,
        _get(clientDetails, 'shortName'),
        _get(clientDetails, 'clientGroup.shortName'),
      )
    : null;
  const isClientExclusive = clientExclusiveLevel === 'Client';
  const isClientGroupExclusive =
    !isClientExclusive && clientExclusiveLevel === 'Client Group';

  let resourceType = type;
  const isArticle = resourceType === 'article';
  const currentPathName = location.pathname.split('/')[1];

  if (isArticle && (item.type === 'News' || item.subtype === 'News')) {
    resourceType = 'news';
  } else if (isArticle && isBlog) {
    resourceType = 'blog';
  }

  if (resourceType === 'topic' && !personalizedCard)
    return (
      <TopicItem
        item={item}
        key={slug}
        listTitle={listTitle}
        evidence={evidence}
        isPdf={isPdf}
        grayscale={grayscale}
        trackingPath={trackingPath}
      />
    );
  if (resourceType === 'link') {
    if (_get(clientDetails, 'metadata.displayNewLinkCard', false))
      return (
        <LinkItemNew
          item={item}
          key={slug}
          listTitle={listTitle}
          isPdf={isPdf}
          grayscale={grayscale}
          trackingPath={trackingPath}
        />
      );

    return (
      <LinkItem
        item={item}
        key={slug}
        listTitle={listTitle}
        isPdf={isPdf}
        grayscale={grayscale}
        trackingPath={trackingPath}
        isClientExclusive={isClientExclusive}
        isClientGroupExclusive={isClientGroupExclusive}
      />
    );
  }

  const isShortSubtitle = resourceType === 'news';

  const matchedLabels = getMatchedLabels(
    item,
    answersMatchedAudiences.concat(profileFilters.audiences),
    answersMatchedTags.concat(profileFilters.tags),
  );

  let finalType = type;
  let subtype;
  let blog;
  const finalRoute = route === '/articles' && isBlog ? '/insights' : route;
  const redirect = e => {
    const redirectUrl = `${finalRoute}/${slug}`;
    const fromAlgolia =
      recommendation || location.pathname.split('/')[1] === 'resources';

    if (type === 'app') {
      const finalSubtype = fromAlgolia ? algoliaSubtype : ctfSubtype;
      if (finalSubtype) {
        if (finalSubtype === 'Online Therapy') subtype = finalSubtype;
        else subtype = 'Apps';
      }
    }
    if (type === 'organization') {
      subtype = fromAlgolia ? algoliaSubtype : ctfSubtype;
    }
    if (
      (type === 'read' || type === 'article') &&
      !isBlog &&
      _get(siteConfig, 'config.articlesMinimumWordCount') !== 0
    ) {
      if (isDescriptionLessThanThreshold) {
        if (!isEmbedded()) {
          e.preventDefault();
          const organizationName =
            item.organization && item.organization.length !== 0
              ? item.organization.find(o => _get(o, 'fields.name'))
              : item.name;
          dispatch(
            setLeavingModal({
              visible: true,
              title,
              url,
              organization:
                typeof organizationName === 'object'
                  ? _get(organizationName, 'fields.name')
                  : organizationName || item.name,
              resourceType: type || null,
              articleURL: redirectUrl,
            }),
          );
        }
      } else {
        finalType = 'article';
        subtype = fromAlgolia ? algoliaSubtype : ctfSubtype;
        blog = isBlog || false;
      }
    }

    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: name || title,
      type: finalType,
      page: finalPageName,
      path: trackingPath || location.pathname.split('/')[1],
      slug: location.pathname.split('/')[2],
      recommendation,
      subtype: subtype || undefined,
      blog,
      listTitle,
      entryId: item.objectID || item.id,
    });
  };

  let file;
  if (!_isEmpty(imageUrl)) file = imageUrl;
  else if (_has(image, 'url')) file = image.url;
  else if (!_has(image, 'fields') && typeof image === 'object') file = {};
  else if (!_isEmpty(image) && typeof image === 'object')
    ({ file } = _get(alternateImage, 'fields')
      ? alternateImage.fields
      : image.fields);
  else file = image;

  const resourceName =
    getAlgoliaLocalizedField(item, 'name', locale) ||
    getAlgoliaLocalizedField(item, 'title', locale);
  const isBook = type === 'book';
  const isVideo = type === 'video';
  const isPerson = type === 'person';
  // const isOrganization = type === 'organization';
  const isList = type === 'list';
  const useMultipleListImages =
    isList && _isEmpty(file) && !_isEmpty(resourcesImages);
  const hasBlurredBackground =
    [
      'app',
      'article',
      'podcast',
      'person',
      'book',
      'news',
      'organization',
      'program',
    ].includes(resourceType) ||
    (isArticle && getBlogType(item) === undefined) ||
    (isList && !useMultipleListImages);
  let imageSrc = _isEmpty(file)
    ? fallbackImage
    : _has(file, 'url')
    ? file.url
    : file;
  if (imageSrc.includes('.crediblemind.com')) {
    imageSrc = fallbackImage;
  }
  let finalImageSrcBackground;
  if (hasBlurredBackground) {
    if (!isIE()) {
      if (imageSrc !== fallbackImage) {
        finalImageSrcBackground = getEmbedlyImageUrl(imageSrc);
      }
    } else if (imageSrc.includes('ctfassets')) {
      finalImageSrcBackground = `${imageSrc}?h=16&w=16`;
    }
  }

  const bookTitleArr = isBook && resourceName.split(':');
  const finalResourceName =
    isBook && !_isNil(bookTitleArr[0])
      ? resourceName.split(':')[0]
      : resourceName;
  let organizationsArray = [];
  if (
    isPerson &&
    !_isEmpty(_get(organization, 'items')) &&
    typeof organization === 'object' &&
    _get(organization, 'fields.reviewStatus') === 'Accepted'
  ) {
    organizationsArray.push(...organization.items);
  } else if (isPerson && !_isEmpty(organization) && organization.length > 0) {
    organizationsArray = organization.filter(
      org => _get(org, 'fields.reviewStatus') === 'Accepted',
    );
  }
  let finalSubtitle;
  if (isBook) {
    [, finalSubtitle] = bookTitleArr;
  } else if (isPerson) {
    if (_isEmpty(organizationsArray)) {
      finalSubtitle = (
        <span>{getAlgoliaLocalizedField(item, 'title', locale)}</span>
      );
    } else {
      finalSubtitle = (
        <>
          <span>{getAlgoliaLocalizedField(item, 'title', locale)}</span>
          <span className={classes.divider}> &#8226; </span>
          {organizationsArray.map((org, index) => {
            if (index !== 0 && index === organizationsArray.length - 1)
              return (
                <span key={_get(org, 'fields.slug')}>
                  &nbsp;and&nbsp;
                  <a href={`/organizations/${_get(org, 'fields.slug')}`}>
                    {_get(org, 'fields.name')}
                  </a>
                </span>
              );
            return (
              <span key={_get(org, 'fields.slug')}>
                <a href={`/organizations/${_get(org, 'fields.slug')}`}>
                  {_get(org, 'fields.name')}
                </a>
              </span>
            );
          })}
        </>
      );
    }
  } else {
    finalSubtitle =
      podcastSubtitle ||
      getAlgoliaLocalizedField(item, 'shortDescription', locale);
  }

  let finalHighlights = [];
  if (cardHighlights)
    finalHighlights = getAlgoliaLocalizedField(item, 'cardHighlights', locale);
  else {
    if (audience)
      getAlgoliaLocalizedField(item, 'audience', locale, []).forEach(el =>
        finalHighlights.push(el),
      );
    if (tags)
      getAlgoliaLocalizedField(item, 'tags', locale, []).forEach(el =>
        finalHighlights.push(_get(el, 'fields.title')),
      );
    if (highlights)
      getAlgoliaLocalizedField(item, 'highlights', locale, []).forEach(el =>
        finalHighlights.push(el),
      );
  }

  const showDescriptionOnExpandedPiece = finalHighlights.length === 0;

  const onListenNotesLoad = subtitle => setPodcastSubtitle(subtitle);
  const bot = isBot();

  const resourceAudiences = [];
  _get(
    item,
    'audienceType',
    getAlgoliaLocalizedField(item, 'audience', locale, []),
  ).forEach(entry => {
    if (
      _has(entry, 'fields.reviewStatus') &&
      _get(entry, 'fields.reviewStatus') !== 'Accepted'
    )
      return;

    const audienceName = _get(entry, 'fields.name', entry);
    resourceAudiences.push(audienceName);
  });
  const resourceTags = [];
  _get(
    item,
    'fields.tags',
    getAlgoliaLocalizedField(item, 'tags', locale, []),
  ).forEach(entry => {
    if (
      _has(entry, 'fields.reviewStatus') &&
      _get(entry, 'fields.reviewStatus') !== 'Accepted'
    )
      return;

    const tagTitle = _get(entry, 'fields.title', entry);
    resourceTags.push(tagTitle);
  });

  let parsedType = expandedType;
  if (!parsedType) {
    parsedType = PARSED_TYPES_MAP[type];
  }
  const hasMatchedLabels = matchedLabels.length > 0;
  const shouldShowRatings =
    !hasMatchedLabels && SHOW_RATINGS_TYPES.includes(parsedType);

  const clientExclusiveLabel = _get(
    clientDetails,
    'metadata.clientExclusiveResource.label',
    '',
  );
  const clientGroupExclusiveLabel = _get(
    clientDetails,
    'clientGroup.metadata.clientExclusiveResource.label',
    clientExclusiveLabel,
  );

  const ImageWrapperComponent =
    isLazyList || isPdf || isEmbedded() ? Box : LazyLoad;
  const imageWrapperProps =
    isLazyList || isPdf ? {} : { height: 123, offset: [100, 0], once: true };

  const paperComponent = () => (
    <Paper
      elevation={0}
      className={_classNames(classes.container, {
        [classes.clientExclusiveContainer]: isClientExclusive,
        [classes.clientGroupExclusiveContainer]: isClientGroupExclusive,
        [classes.personalizedContainer]: personalizedCard,
        [classes.fromClientAdminPersonalizedContainer]: fromClientAdmin,
        [classes.openContainer]: !expanded && open,
      })}
      onMouseEnter={() => {
        if (!hasTracked.current) {
          hasTracked.current = true;
          UserInteractionService.registerGeneralInteraction({
            type: 'resource-card-hovered',
            resourceID: item.objectID || item.id,
            resourceAudiences: resourceAudiences.join(),
            resourceTags: resourceTags.join(),
            resourceType,
          });
        }
      }}
    >
      <div className={classes.desktopWrapper}>
        <Grid container component={personalizedCard ? CardActionArea : Grid}>
          <Grid
            item
            xs={12}
            className={_classNames(classes.imageVideoContainer, {
              [classes.personalizeImageContainer]: personalizedCard,
              [classes.videoContainer]: isVideo,
            })}
          >
            {!_isNil(finalImageSrcBackground) &&
              !personalizedCard &&
              _isEmpty(alternateImage) && (
                <div
                  className={_classNames(classes.blurredBackground, {
                    [classes.blurredGrayBackground]: grayscale,
                  })}
                  style={{
                    backgroundImage: `url(${finalImageSrcBackground})`,
                  }}
                />
              )}
            {resourceType === 'video' ? (
              <ImageWrapperComponent
                {...imageWrapperProps}
                className={classes.imageWrapper}
              >
                <VideoEmbedly
                  title={title}
                  url={url}
                  imageUrl={file}
                  key={url}
                  isSmallCard
                  id={item.objectID || item.id}
                  trackingPath={trackingPath}
                  route={finalRoute}
                  slug={slug}
                />
              </ImageWrapperComponent>
            ) : resourceType === 'podcast' ? (
              <ImageWrapperComponent
                {...imageWrapperProps}
                className={classes.imageWrapper}
              >
                <AudioCard
                  data={{ ...item, id: item.objectID || item.id }}
                  file={file}
                  description={shortDescription}
                  name={finalResourceName}
                  resourceType={resourceType}
                  labels={labels}
                  onLoad={onListenNotesLoad}
                  isSmallCard
                  expanded={expanded || open}
                  grayscale={grayscale}
                  trackingPath={trackingPath}
                />
              </ImageWrapperComponent>
            ) : useMultipleListImages ? (
              <ListImage images={resourcesImages} grayscale={grayscale} />
            ) : personalizedCard && !_isEmpty(alternateImage) ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img
                alt="Personalized Image"
                src={_get(alternateImage, 'fields.file.url')}
                className={_classNames(classes.cardImg, {
                  [classes.grayscale]: grayscale,
                })}
              />
            ) : (
              <div className={classes.imageWrapper}>
                <Image
                  file={file}
                  type={resourceType}
                  isLazyList={isLazyList}
                  personalizedCard={personalizedCard}
                  isPdf={isPdf}
                  grayscale={grayscale}
                />
              </div>
            )}
            {clientExclusiveLevel && (
              <Box
                className={_classNames(classes.clientExclusiveLabelWrapper, {
                  [classes.clientGroupExclusiveLabelWrapper]: isClientGroupExclusive,
                })}
              >
                <Typography
                  className={_classNames(classes.clientExclusiveLabel, {
                    [classes.clientGroupExclusiveLabel]: isClientGroupExclusive,
                  })}
                >
                  {isClientGroupExclusive
                    ? clientGroupExclusiveLabel
                    : clientExclusiveLabel}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            className={_classNames(classes.contentWrapper, {
              [classes.openContentWrapper]: open,
            })}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                className={_classNames({
                  [classes.contentRatingWrapper]: shouldShowRatings,
                })}
              >
                <Grid container>
                  {!personalizedCard && (
                    <Grid item xs={12} className={classes.badgeAndIconWrapper}>
                      <Badge
                        type={resourceType}
                        minutes={minutes}
                        removeMargin
                        data={item}
                      />

                      {!bot && allowSignIn && (
                        <>
                          {saved && (
                            <IconButton
                              className={classes.iconButton}
                              onClick={handleSave}
                              disableRipple
                              disableFocusRipple
                            >
                              <img
                                src={HeartFullIcon}
                                alt="Full"
                                className={classes.icon}
                              />
                            </IconButton>
                          )}
                          {!saved && (
                            <IconButton
                              className={classes.iconButton}
                              onClick={handleSave}
                              disableRipple
                              disableFocusRipple
                            >
                              <img
                                src={HeartEmptyIcon}
                                alt="Empty"
                                className={classes.icon}
                              />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Grid>
                  )}
                  <Title
                    title={finalResourceName}
                    type={resourceType}
                    personalizedCard={personalizedCard}
                  />
                  <Grid item xs={12}>
                    {!personalizedCard && (
                      <Typography
                        color="textPrimary"
                        align="left"
                        className={_classNames(classes.subtitle, {
                          [classes.shortSubtitle]: isShortSubtitle,
                          [classes.personSubtitle]:
                            isPerson && !showDescriptionOnExpandedPiece,
                          [classes.personSubtitleExpanded]:
                            isPerson && showDescriptionOnExpandedPiece,
                        })}
                      >
                        {finalSubtitle}
                      </Typography>
                    )}
                  </Grid>
                  {!personalizedCard &&
                    !showDescriptionOnExpandedPiece &&
                    currentPathName !== 'resources' &&
                    (isPerson || isList) && (
                      <Grid item xs={12} className={classes.personBio}>
                        <RichTextReactRenderer data={description} type="name" />
                      </Grid>
                    )}
                  {hasMatchedLabels && (
                    <Grid item xs={12}>
                      <MatchedLabels labels={matchedLabels} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {shouldShowRatings && (
                <Grid item xs={12}>
                  <Rating type={parsedType} data={item} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Paper>
  );

  return (
    <div
      className={_classNames(classes.wrapper, {
        [classes.openWrapper]: !expanded && open,
      })}
      ref={wrapperRef}
    >
      {asLink ? (
        <Link
          to={`${finalRoute}/${slug}`}
          className={classes.link}
          onClick={redirect}
        >
          {paperComponent()}
        </Link>
      ) : (
        paperComponent()
      )}
    </div>
  );
}

ResourceItemSmall.propTypes = {
  item: PropTypes.object,
  type: PropTypes.string,
  route: PropTypes.string,
  listTitle: PropTypes.string,
  recommendation: PropTypes.bool,
  expanded: PropTypes.bool,
  evidence: PropTypes.bool,
  isLazyList: PropTypes.bool,
  isPdf: PropTypes.bool,
  grayscale: PropTypes.bool,
};

ResourceItemSmall.defaultProps = {
  recommendation: false,
  expanded: false,
  evidence: false,
  isPdf: false,
};

export default React.memo(ResourceItemSmall);
